import 'react';
import {
	Alert,
	Button,
	Card,
	Checkbox,
	Descriptions,
	Empty,
	Flex,
	Form,
	InputNumber,
	message,
	Modal,
	notification,
	Table,
	Tag,
	Tooltip,
	Typography,
	Select,
} from 'antd';

import { useNavigate, useParams, useLocation } from 'react-router';
import { ShopShipDate } from '../../../components/ShopShipDate';
import {
	useGetOrderLineItemByIdV2Query,
	useUpdateOrderLineItemMutation,
	useGetCamDataQuery,
} from '../../../redux/api/order';
import { useCreateVSCProgramMutation } from '../../../redux/api/versabuilt';
import { useEffect, useState } from 'react';
import {
	ArrowRightOutlined,
	PlusOutlined,
	QuestionCircleTwoTone,
} from '@ant-design/icons';
import { useCreateManufacturingRouteMutation } from '../../../redux/api/order-line-item.ts';
import { ManufacturingProcess } from '../../../interfaces/Manufacturing.Interface.ts';
import Loading from '../../../components/Loading/index.tsx';
import { Link } from 'react-router-dom';
import AddManufacturingProcessModal from '../../../components/AddManufacturingProcessModal/index.tsx';
import {
	closeAddManufacturingProcessModal,
	showAddManufacturingProcessModal,
} from '../../../redux/slices/manufacturing.ts';
import { useDispatch } from 'react-redux';

import Header from '../../../components/Header/index.tsx';
import BuildingTag from '../../../components/BuildingTag';
import { InternalTag } from '../../../components/OrderLineItemsTable/InternalTag';
import { useHasMultipleBuildings } from '../../../hooks/useHasMultipleBuildings';
import { useCanViewRevenue } from '../../../hooks/useCanViewRevenue';
import { useCanManageInspectionSampleLabeling } from '../../../hooks/useCanManageInspectionSampleLabeling';
import dayjs from 'dayjs';

import OrderLineItemStatuses from '../OrderLineItemStatuses/index.tsx';
import { PartDimensions } from '../PartDimensions';
import { FileDownloads } from '../FileDownloads';
import Thumbnail from '../Thumbnail/index.tsx';
import { getOrderLineItemStatusTagColor } from '../../../utils';
import OrderLineItemsTable from '../../../components/OrderLineItemsTable/index.tsx';
import ProductionBooleanSelect from './ProductionBooleanSelect';
import { MaterialReservations } from '../MaterialReservations/index.tsx';
import MaterialRequests from '../MaterialRequests/index.tsx';
import { MaterialSelection } from '../MaterialSelection/index.tsx';
import Fusion360SetupData from '../../../components/Fusion360SetupData/index.tsx';
import { QualityButtons } from '../../../components/QualityButtons/index.tsx';
import { getProductionMethodTagColor } from '../../../utils';
import SampleMatrixTable from '../../../components/Operations/Quality/SampleMatrixTable/';
import { ProductionTravelerButton } from '../../../components/QualityButtons/ProductionTravelerButton.tsx';
import KeyencePrograms from '../../../components/Operations/Quality/KeyencePrograms/index.tsx';
import { BallooningFAIButton } from '../../../components/QualityButtons/BallooningFAIButton.tsx';
import { MaterialCertificatesButton } from '../../../components/MaterialInventory/MaterialCertificatesButton.tsx';
import { QualityPacketButton } from '../../../components/QualityButtons/QualityPacketButton.tsx';
import { SyncToVendorAppButton } from '../../../components/QualityButtons/SyncToVendorAppButton.tsx';
import OrderLineItemDocuments from '../OrderLineItemDocuments';
import OrderLineItemNotes from '../OrderLineItemNotes';
import OrderLineItemTickets from '../OrderLineItemTickets';
import ProductionQuestions from '../ProductionQuestions';
import RobotPrograms from '../RobotPrograms';
import LineItemKitting from '../LineItemKitting';
import DeliveryList from '../../../components/Operations/Delivery/DeliveryList/index.tsx';
import useControlsDisabled from '../../../hooks/useControlsDisabled';
import { useGetAqlTablesQuery } from '../../../redux/api/quality.ts';
import MachineOverrideSelect from '../../Scheduling/_shared/components/MachineOverrideSelect';
import ProgramScoringCard from '../../CamProgramming/ProgramScoringCard';
import {
	getInspectionProjectId,
	shouldShowLotInspection,
} from '../../../utils/inspectionProjectId';
import { extractApiError } from '../../../utils/apiError';
import LineItemProcessSelect from '../../../components/ProcessCatalog/LineItemProcessSelect';
import SpecialProcessTag from '../../../components/ProcessCatalog/SpecialProcessTag';

const SWISS = 'Swiss';
const FASTLANE = 'Fastlane';

const isFastlaneProductionMethod = (method?: string | null) =>
	(method ?? '').trim().toLowerCase() === FASTLANE.toLowerCase();
const { Title, Text } = Typography;
const { Option } = Select;

const LABELING_TOOLTIP =
	"Every inspected sample must be bagged and labeled with its sample number and the operator's initials. Operators are asked to confirm this at the hand and Keyence checks and again before the delivery tag prints.";

/** Read-only rather than hidden for non-managers: operators work this page for
 * material reservations and a stray click would silently change what quality
 * and delivery demand of the job, but they still need to see the requirement. */
const LABELING_READ_ONLY_TOOLTIP = `${LABELING_TOOLTIP} Only Shop Managers can change this.`;

const OrderLineItemDetails = () => {
	const { id } = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const hasMultipleBuildings = useHasMultipleBuildings();
	const canViewRevenue = useCanViewRevenue();
	const canManageLabeling = useCanManageInspectionSampleLabeling();

	const [notify, notificationContext] = notification.useNotification();
	const [messageApi, messageContext] = message.useMessage();
	const [isDisabled, setIsDisabled] = useState<boolean>(false);
	const [selectedValue, setSelectedValue] = useState('');
	const [revenue, setRevenue] = useState<number | null>(null);
	const [isUpdatingRevenue, setIsUpdatingRevenue] = useState(false);

	const [, modalContext] = Modal.useModal();

	const [form] = Form.useForm();
	const [createVSCProgramForm] = Form.useForm();

	const [
		confirmVSCProgramCreationModalVisible,
		setConfirmVSCProgramCreationModalVisible,
	] = useState<boolean>(false);

	const [selectedProcess, setSelectedProcess] =
		useState<ManufacturingProcess>();

	const { data, isLoading, refetch } = useGetOrderLineItemByIdV2Query(
		{ id: id as string },
		{
			skip: !id,
			refetchOnMountOrArgChange: true,
			pollingInterval: 60000,
		}
	);

	// CAM data is fetched separately from the v2 line item so the heavy
	// cam_data JSON does not block the main page render. The CAM Program Data
	// section paints independently once this request settles.
	const {
		data: camDataExports,
		isLoading: isLoadingCamDataExports,
		isFetching: isFetchingCamDataExports,
	} = useGetCamDataQuery(
		{ id: id as string },
		{
			skip: !id,
			refetchOnMountOrArgChange: true,
		}
	);

	// Unified disabled state based on role + status
	const { controlsDisabled } = useControlsDisabled(data?.status);

	const [updateOrderLineItem, updateOrderLineItemResult] =
		useUpdateOrderLineItemMutation();

	const [createVSCProgram, createVSCProgramResult] =
		useCreateVSCProgramMutation();

	const [programsRefreshTick, setProgramsRefreshTick] = useState(0);
	const { data: aqlTables } = useGetAqlTablesQuery();

	const disableMaterialCertificates =
		(data?.material_ordering_status !== 'Completed' &&
			data?.material_selection_status !== 'Completed' &&
			data?.material_prep_status !== 'Completed') ||
		!data?.has_a_material_certificate;

	async function onClickCreateVSCProgram(
		id: string,
		overwrite_current: boolean
	) {
		await createVSCProgram({
			id: id,
			body: {
				order_line_item: data?.id,
				manufacturing_process: selectedProcess?.id,
				overwrite_current: overwrite_current,
			},
		});
	}

	const saveProductionBooleanField = async (
		values: Record<string, boolean>
	) => {
		if (!data?.id) {
			return;
		}

		await updateOrderLineItem({
			id: data.id,
			values,
		});

		refetch();
	};

	const onClickCreateVSCProgramHandler = async (
		process: ManufacturingProcess
	) => {
		if (process?.machine?.mill_automation_system?.id) {
			const fields = await createVSCProgramForm.validateFields();

			onClickCreateVSCProgram(
				process.machine?.mill_automation_system?.id,
				fields.overwrite_current
			);
		}
	};

	const [createManufacturingRoute, result] =
		useCreateManufacturingRouteMutation();

	const onFormSubmit = async (values: ManufacturingProcess) => {
		await createManufacturingRoute({
			id: id,
			body: {
				...values,
				setups: values.setups.map((setup, i) => {
					return {
						...setup,
						setup_number: i + 1,
					};
				}),
			},
		});
	};

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const tab = params.get('tab');
		if (
			tab &&
			tab !== 'quality' &&
			tab !== 'production-questions' &&
			location.search
		) {
			navigate(window.location.pathname, { replace: true });
		}
	}, [location.search]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const tab = params.get('tab');
		const sectionId =
			tab === 'quality'
				? 'quality-section'
				: tab === 'production-questions'
				? 'production-questions-section'
				: null;

		if (!sectionId) {
			return;
		}

		const tryScroll = (attempt = 0) => {
			const el = document.getElementById(sectionId);
			if (el) {
				el.scrollIntoView({ behavior: 'smooth', block: 'start' });
				setTimeout(
					() => navigate(window.location.pathname, { replace: true }),
					300
				);
				return;
			}
			if (attempt < 10) {
				setTimeout(() => tryScroll(attempt + 1), 100);
			}
		};
		setTimeout(() => tryScroll(0), 50);
	}, [location.search, data]);

	useEffect(() => {
		if (result.isSuccess) {
			form.resetFields();
			dispatch(closeAddManufacturingProcessModal());
			refetch();
		}
	}, [result]);

	useEffect(() => {
		if (updateOrderLineItemResult.isSuccess) {
			messageApi.open({
				type: 'success',
				content: 'Line item updated successfully',
			});
		}
		if (updateOrderLineItemResult.isError) {
			messageApi.open({
				type: 'error',
				content: extractApiError(updateOrderLineItemResult.error),
			});
		}
	}, [updateOrderLineItemResult]);

	useEffect(() => {
		if (createVSCProgramResult.isSuccess) {
			createVSCProgramForm.resetFields();
			setConfirmVSCProgramCreationModalVisible(false);
		}

		if (createVSCProgramResult.isError) {
			notify.error({
				message: 'An Error Occurred',
				description: extractApiError(createVSCProgramResult.error),
			});
			setConfirmVSCProgramCreationModalVisible(false);
		}
	}, [createVSCProgramResult]);

	useEffect(() => {
		if (data) {
			setIsDisabled(
				data?.status === 'On Hold' ||
					data?.status === 'Completed' ||
					data?.status === 'Cancelled'
			);
		}
	}, [data]);

	useEffect(() => {
		if (data?.revenue !== undefined) {
			setRevenue(data.revenue != null && data.revenue !== '' ? Number(data.revenue) : null);
		}
	}, [data?.revenue]);

	const handleUpdateProductionMethod = async () => {
		if (data?.id) {
			const payload = {
				id: data.id,
				values: { partsbadger_production_method: selectedValue },
			};

			updateOrderLineItem(payload)
				.unwrap()
				.then(() => {
					messageApi.open({
						type: 'success',
						content: 'Production method updated successfully',
					});
					refetch();
				})
				.catch((e) =>
					messageApi.open({ type: 'error', content: extractApiError(e) })
				);
		}
	};

	const handleRevenueChange = (value: number | null) => {
		setRevenue(value);
	};

	const commitRevenueChange = async () => {
		if (!data?.id) {
			return;
		}

		// Only persist when the value actually changed, so blur without edits
		// doesn't trigger an unnecessary PATCH.
		const originalRevenue =
			data?.revenue != null && data.revenue !== '' ? Number(data.revenue) : null;
		if (revenue === originalRevenue) {
			return;
		}

		setIsUpdatingRevenue(true);
		try {
			await updateOrderLineItem({
				id: data.id,
				values: {
					revenue,
				},
			}).unwrap();

			// Refetch to get updated calculated values
			refetch();
			messageApi.open({ type: 'success', content: 'Revenue updated successfully' });
		} catch (error: any) {
			messageApi.open({ type: 'error', content: extractApiError(error) });
			// Revert to original value on error
			setRevenue(originalRevenue);
		} finally {
			setIsUpdatingRevenue(false);
		}
	};

	const formatCurrency = (value: number | null | undefined): string => {
		if (value === null || value === undefined) {
			return 'N/A';
		}
		return `$${Number(value).toFixed(2)}`;
	};

	// Temporarily unused - for Material Cost % (commented out in UI)
	// const formatPercent = (value: number | null | undefined): string => {
	// 	if (value === null || value === undefined) {
	// 		return 'N/A';
	// 	}
	// 	return `${Number(value).toFixed(2)}%`;
	// };

	return (
		<>
			<Header
				title="Order Line Item Details"
				breadcrumbItems={[
					{
						title: <Link to="/"> Home </Link>,
					},
					{
						title: <Link to="/orders"> Orders </Link>,
					},
					{
						title: (
							<Link to={`/orders/${data?.order?.id}/`}> Order Details </Link>
						),
					},
					{
						title: 'Order Line Item Details',
					},
				]}
			/>
			<div>
				{!data || isLoading ? (
					<Loading loading={true} />
				) : (
					<>
						<div className="w-full">
							<div className="flex flex-col md:flex-row gap-3 mb-10 items-stretch">
								<Card className="flex-grow w-fit self-stretch">
									<div className="flex flex-col lg:flex-row gap-5">
										<div className="sm:w-80 lg:w-72">
											<Thumbnail lineItem={data} />
										</div>
										<div>
											<div className="flex flex-row justify-between">
												<Title level={5}>Details</Title>
												<div>
													{data?.partsbadger_production_method !== null &&
													data?.partsbadger_production_method.trim() !== '' &&
													data?.partsbadger_production_method.toLowerCase() !==
														'no' ? (
														<Tag
															color={getProductionMethodTagColor(
																data?.partsbadger_production_method
															)}
														>
															{data?.partsbadger_production_method}
														</Tag>
													) : (
														<div>
															<Select
																value={selectedValue}
																onSelect={setSelectedValue}
															>
																<Option value="">
																	Select Production Method
																</Option>
																<Option value={'Fastlane'}>Fastlane</Option>
																<Option value={'QuickTurn'}>QuickTurn</Option>
																<Option value={'Swiss'}>Swiss</Option>
																<Option value={'Traditional'}>
																	Traditional
																</Option>
															</Select>
															{selectedValue && (
																<Button
																	type="primary"
																	className="ml-2"
																	onClick={handleUpdateProductionMethod}
																	loading={updateOrderLineItemResult.isLoading}
																	disabled={updateOrderLineItemResult.isLoading}
																>
																	Confirm
																</Button>
															)}
														</div>
													)}
													<InternalTag isInternal={data?.is_internal} />
													{data?.order?.is_draft && (
														<Tag color="gold">Draft</Tag>
													)}
												</div>
											</div>
											<div className="flex flex-wrap mb-5 gap-x-8 gap-y-8">
												<Flex vertical={true} align="start">
													<span className="text-slate-400">Item Name</span>
													<span>{data?.name}</span>
												</Flex>

												<Flex vertical={true} align="start">
													<span className="text-slate-400">Order Name</span>
													<Link to={`/orders/${data?.order?.id}`}>
														{data?.order?.name}
													</Link>
												</Flex>

												<Flex vertical={true} align="start">
													<span className="text-slate-400">Material</span>
													<span>{data?.material_name}</span>
												</Flex>

												<Flex vertical={true} align="start">
													<span className="text-slate-400">Surface Finish</span>
													{/* The tag replaces the text rather than joining it:
													    both say the same thing, and the badge is what
													    makes a Bead Blast job look different from an
													    as-machined one. */}
													{data?.process_requirements?.finishing?.is_special ? (
														<SpecialProcessTag
															kind="finishing"
															requirement={data.process_requirements.finishing}
															note={data?.finish_name}
															showBatches
														/>
													) : (
														<span>{data?.finish_name}</span>
													)}
												</Flex>

												<Flex vertical={true} align="start">
													<span className="text-slate-400">Coatings</span>
													<span>{data?.coating_name}</span>
												</Flex>

												{hasMultipleBuildings && (
													<Flex vertical={true} align="start">
														<span className="text-slate-400">Building</span>
														{data?.locked_building ? (
															<BuildingTag name={data.locked_building.name} />
														) : (
															<Text type="secondary">—</Text>
														)}
													</Flex>
												)}

												<Flex vertical={true} align="start">
													<span className="text-slate-400">Quantity</span>
													{data?.cancelled_quantity > 0 ? (
														<div className="flex flex-row items-baseline gap-1">
															<Text delete>{data?.quantity}</Text>
															<Text>
																<ArrowRightOutlined
																	style={{ fontSize: '0.7rem' }}
																/>
															</Text>
															<Text strong mark>
																{data?.updated_quantity}
															</Text>
														</div>
													) : (
														<span>{data?.quantity}</span>
													)}
												</Flex>

												<Flex vertical={true} align="start">
													<span className="text-slate-400">
														Required Shop Ship Date{' '}
														<Tooltip title="This is the required ship date for PartsBadger Production">
															<QuestionCircleTwoTone />
														</Tooltip>
													</span>

													<span>
														<ShopShipDate
															original={data.required_partner_ship_date}
															revised={data.revised_partner_ship_date}
															format="dddd, MMM D YYYY"
														/>
													</span>
												</Flex>

												<Flex vertical={true} align="start">
													<span className="text-slate-400">
														Required Part Ship Date{' '}
														<Tooltip title="This is the date show to the customer on the sales order">
															<QuestionCircleTwoTone />
														</Tooltip>
													</span>

													<span className="font-bold">
														{dayjs(data.required_part_ship_date).format(
															'dddd, MMM D YYYY'
														)}
													</span>
												</Flex>

												{data.partsbadger_production_method === SWISS && (
													<Flex vertical={true} align="start">
														<span className="text-slate-400">Program Name</span>
														<span className="font-bold">
															{data?.swiss_program_name}
														</span>
													</Flex>
												)}
											</div>

											<div className="flex flex-wrap mb-5 gap-x-8 gap-y-8">
												<Flex vertical={true} align="start">
													<span className="text-slate-400">Material Cost</span>
													<span className="font-semibold">
														{formatCurrency(
															(data as any)?.material_cost != null &&
																(data as any).material_cost !== ''
																? Number((data as any).material_cost)
																: null
														)}
													</span>
												</Flex>

												{canViewRevenue && (
												<Flex vertical={true} align="start">
													<span className="text-slate-400">Revenue</span>
													<InputNumber
														value={revenue}
														onChange={handleRevenueChange}
														onBlur={commitRevenueChange}
														onPressEnter={commitRevenueChange}
														disabled={controlsDisabled || isUpdatingRevenue}
														formatter={(value) =>
															`$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
														}
														parser={((value?: string) => {
															const parsed = (value ?? '').replace(/\$\s?|(,*)/g, '').trim();
															// Empty input clears revenue back to NULL
															// (distinct from an explicit 0).
															return parsed === '' ? null : Number(parsed);
														}) as any}
														precision={2}
														min={0}
														style={{ width: '150px' }}
														placeholder="Enter revenue"
													/>
												</Flex>
											)}
											</div>

											<div className="mb-10">
												<PartDimensions
													height={data?.height}
													width={data?.width}
													length={data?.length}
													volume={data?.volume}
													bbox_volume={data?.bbox_volume}
												/>
											</div>
											<div className="flex flex-col sm:flex-row flex-wrap items-start gap-y-8 gap-x-16">
												<div>
													<FileDownloads
														lineItem={data}
														refetchOrder={refetch}
														isDisabled={
															data?.status === 'On Hold' ||
															data?.status === 'Completed' ||
															data?.status === 'Cancelled'
														}
														enableBalloonedDrawings={false}
													/>
												</div>
												<div className="max-w-sm">
													<Title level={5}>Quoting Notes</Title>
													<span>
														{data?.partsbadger_production_quoting_notes
															? data.partsbadger_production_quoting_notes
															: 'None'}
													</span>
												</div>
												<div className="max-w-sm">
													<Title level={5}>Vendor Requirements</Title>
													<Flex wrap="wrap" gap="small">
														{data?.vendor_requirements &&
														data?.vendor_requirements?.length > 0 ? (
															data?.vendor_requirements?.map((requirement) => (
																<Tag
																	key={requirement}
																	color="gold-inverse"
																	style={{ margin: 0 }}
																>
																	{requirement}
																</Tag>
															))
														) : (
															<>None</>
														)}
													</Flex>
												</div>
											</div>
											<div className="mt-8 w-full">
												<Title level={5}>Production</Title>
												<div className="flex flex-col gap-3">
													<LineItemProcessSelect
														kind="handling"
														layout="inline"
														lineItem={data}
														disabled={controlsDisabled}
														refetch={refetch}
													/>
													<LineItemProcessSelect
														kind="cleaning"
														layout="inline"
														lineItem={data}
														disabled={controlsDisabled}
														refetch={refetch}
													/>
													<ProductionBooleanSelect
														label="Keyence Program Required"
														value={data?.is_keyence_program_required}
														disabled={isDisabled}
														saving={updateOrderLineItemResult.isLoading}
														onSave={(checked) =>
															saveProductionBooleanField({
																is_keyence_program_required: checked,
															})
														}
													/>
													<ProductionBooleanSelect
														label="Additional Tooling Required"
														value={data?.is_additional_tooling_required}
														disabled={isDisabled}
														saving={updateOrderLineItemResult.isLoading}
														onSave={(checked) =>
															saveProductionBooleanField({
																is_additional_tooling_required: checked,
															})
														}
													/>
													<ProductionBooleanSelect
														label="Inspection Sample Labeling"
														value={data?.requires_inspection_sample_labeling}
														disabled={isDisabled || !canManageLabeling}
														saving={updateOrderLineItemResult.isLoading}
														tooltip={
															canManageLabeling
																? LABELING_TOOLTIP
																: LABELING_READ_ONLY_TOOLTIP
														}
														onSave={(checked) =>
															saveProductionBooleanField({
																requires_inspection_sample_labeling: checked,
															})
														}
													/>
												</div>
											</div>
										</div>
									</div>
								</Card>
								<Card className="w-full md:w-96 self-stretch">
									<Flex
										align="baseline"
										justify="space-between"
										className="mb-3"
									>
										<Title level={5}>Production Status</Title>

										<Tag color={getOrderLineItemStatusTagColor(data?.status)}>
											{data?.status}
										</Tag>
									</Flex>
									<OrderLineItemStatuses
										disabled={isDisabled}
										lineItem={data}
										refetchLineItem={refetch}
									/>
								</Card>
							</div>

							{data?.id && (
								<Flex vertical className="w-full">
									<div id="production-questions-section" className="mb-10">
										<Title level={4}>Production Questions</Title>
										<Card>
											<ProductionQuestions
												lineItemId={data.id as string}
												disabled={isDisabled}
											/>
										</Card>
									</div>
								</Flex>
							)}

							<Flex vertical className="w-full">
								<Title level={4}>Deliveries</Title>
								<Card>
									<DeliveryList item={data} />
								</Card>
							</Flex>

							<Flex vertical className="w-full">
								<div className="mt-10">
									<Title level={4}>Material Information</Title>
									{data?.order?.is_draft ? (
										/*
										 * Draft orders are non-production (PTB-6481): the backend
										 * rejects reservations/requests for them, so the controls
										 * are not offered at all. Prep flows (ballooning, quality,
										 * CAM, kitting statuses) stay available on this page by
										 * design.
										 */
										<Alert
											type="info"
											showIcon
											message="This internal order is still a draft"
											description="Material selection, reservations and requests unlock once the order is finalized from its draft banner."
										/>
									) : (
																			<Card>
											<div>
												<MaterialSelection
													lineItem={data}
													refetchLineItem={refetch}
													actionsDisabled={controlsDisabled}
												/>
											</div>
											<div className="mt-5">
												<MaterialReservations
													lineItem={data}
													refetchLineItem={refetch}
												/>
											</div>
											<div className="mt-5">
												<MaterialRequests
													lineItem={data}
													refetchLineItem={refetch}
													actionsDisabled={controlsDisabled}
												/>
											</div>
										</Card>
									)}
								</div>
							</Flex>
							<Flex vertical className="w-full ">
								<div className="mt-10">
									<Flex justify="space-between" align="baseline">
										<Title level={4}>Scheduling</Title>
										{data?.scheduling_job_id && (
											<Link
												to={`/scheduling/jobs/${data.scheduling_job_id}`}
												target="_blank"
											>
												<Button type="link" size="small">
													View Job <ArrowRightOutlined />
												</Button>
											</Link>
										)}
									</Flex>
									{data?.scheduling_job_id && (
										<div className="my-3">
											<span className="text-slate-400 block mb-1">
												Machine Override
											</span>
											<MachineOverrideSelect
												jobId={Number(data.scheduling_job_id)}
												disabled={controlsDisabled}
											/>
										</div>
									)}
									<Card>
										<div className="flex flex-wrap gap-x-10 gap-y-6">
											<Flex vertical={true} align="start">
												<span className="text-slate-400">Run Difficulty</span>
												<InputNumber
													value={data?.run_difficulty}
													min={1}
													max={10}
													disabled={controlsDisabled}
													style={{ width: 80 }}
													onChange={(value) => {
														if (value !== null && value !== undefined) {
															updateOrderLineItem({
																id: data.id,
																values: { run_difficulty: value },
															});
														}
													}}
												/>
											</Flex>
											<Flex vertical={true} align="start">
												<span className="text-slate-400">
													Machine Setup (min)
												</span>
												<InputNumber
													value={
														data?.machine_setup_time_minutes != null
															? Number(data.machine_setup_time_minutes)
															: null
													}
													min={0}
													disabled={controlsDisabled}
													style={{ width: 100 }}
													onChange={(value) => {
														if (value !== null && value !== undefined) {
															updateOrderLineItem({
																id: data.id,
																values: { machine_setup_time_minutes: value },
															});
														}
													}}
												/>
											</Flex>
											<Flex vertical={true} align="start">
												<span className="text-slate-400">
													Machine Time (min)
												</span>
												<span className="font-semibold">
													{data?.machine_time_minutes != null
														? Number(data.machine_time_minutes).toFixed(1)
														: 'N/A'}
												</span>
											</Flex>
											<Flex vertical={true} align="start">
												<span className="text-slate-400">
													Material Cutting (min)
												</span>
												<span className="font-semibold">
													{data?.material_cutting_time_minutes != null
														? Number(
																data.material_cutting_time_minutes
														  ).toFixed(1)
														: 'N/A'}
												</span>
											</Flex>
										</div>
									</Card>
								</div>

								<div id="quality-section" className="mt-10">
									<Title level={4}>Quality</Title>
									<Card>
										<div style={{ marginBottom: 24 }}>
											<Flex align="center" gap="middle">
												<Text strong>AQL Table</Text>
												<Select
													style={{ width: 260 }}
													value={data?.aql_table ?? undefined}
													placeholder="Default"
													allowClear
													disabled={
														controlsDisabled ||
														data.ballooning_status !== 'Not Started'
													}
													onChange={(value) => {
														updateOrderLineItem({
															id: data.id,
															values: { aql_table: value ?? null },
														})
															.unwrap()
															.then(() => {
																refetch();
																messageApi.open({
																	type: 'success',
																	content: 'AQL table updated',
																});
															})
															.catch((e: any) => {
																messageApi.open({
																	type: 'error',
																	content: extractApiError(e),
																});
															});
													}}
												>
													{aqlTables?.map((table) => (
														<Option key={table.id} value={table.id}>
															{table.name}
														</Option>
													))}
												</Select>
											</Flex>
										</div>
										<div style={{ marginBottom: 24 }}>
											<QualityButtons
												lineItem={data}
												isDisabled={data.ballooning_status !== 'Completed'}
												uploadDisabled={controlsDisabled}
												refetchOrder={async () => {
													await refetch();
													setProgramsRefreshTick((t) => t + 1);
												}}
											/>
										</div>

										{data?.is_keyence_program_required &&
											getInspectionProjectId(data) && (
												<div style={{ marginBottom: 24 }}>
													<KeyencePrograms
														key={`${getInspectionProjectId(
															data
														)}-${programsRefreshTick}`}
														projectId={getInspectionProjectId(data)!}
													/>
												</div>
											)}

										{shouldShowLotInspection(data) && (
											<div>
												<SampleMatrixTable
													key={`${getInspectionProjectId(data)}-${
														data.modified_time
													}`}
													projectId={getInspectionProjectId(data)}
													orderLineItemId={data.id}
													resetDisabled={controlsDisabled}
												/>
											</div>
										)}
									</Card>
								</div>
							</Flex>
							<Flex vertical className="w-full">
								<div id="program-scoring-section" className="mb-10 mt-10">
									{/* The heading lives inside the card, which sits a layout
									    switch beside it. */}
									{data?.id && isFastlaneProductionMethod(data.partsbadger_production_method) && (
										<ProgramScoringCard
											orderLineItemId={data.id as string}
											orderLineItemName={data.name}
											onStatsLogged={refetch}
										/>
									)}
								</div>
							</Flex>
							<Flex vertical className="w-full">
								<div className="mb-10 mt-10">
									{data?.partsbadger_production_method === SWISS ? (
										<>
											<Title level={4}>Program Data</Title>
											<Card>
												<Flex vertical align="start">
													<span>Program Name</span>
													<span className="font-bold">
														{data?.swiss_program_name}
													</span>
												</Flex>
											</Card>
											{data?.id && (
												<>
													<OrderLineItemDocuments
														lineItemId={data.id as string}
													/>
													<OrderLineItemNotes lineItemId={data.id as string} />
												</>
											)}
										</>
									) : (
										<>
											<Title level={4}>CAM Program Data</Title>
											{isLoadingCamDataExports ? (
												<Card loading />
											) : camDataExports && camDataExports.length > 0 ? (
												<Fusion360SetupData data={camDataExports} />
											) : (
												<Card loading={isFetchingCamDataExports}>
													<Empty description="No CAM Program Data Exported" />
												</Card>
											)}
										</>
									)}
								</div>
							</Flex>

							{data?.id && (
								<Flex vertical className="w-full">
									<LineItemKitting
										lineItemId={data.id as string}
										printDisabled={controlsDisabled}
										kittingStarted={['In Progress', 'Completed'].includes(
											data.cart_assembly_status
										)}
										hasSetupSheet={
											camDataExports ? camDataExports.length > 0 : undefined
										}
										onPrinted={refetch}
									/>
								</Flex>
							)}

							{data?.id && (
								<Flex vertical className="w-full">
									<div id="robot-programs-section" className="mb-10">
										<Title level={4}>Robot Programs</Title>
										<RobotPrograms
											orderLineItemId={data.id as string}
											orderLineItemName={data.name}
											orderName={data.order?.name}
											programs={data.vsc_part_data}
											disabled={controlsDisabled}
										/>
									</div>
								</Flex>
							)}

							{/*
							 * Outside the Swiss/Fastlane fork above: a problem at the
							 * machine happens either way, and Notes only appearing on
							 * the Swiss path is already a gap people work around.
							 * Renders nothing when the job has no tickets, which is
							 * most of them.
							 */}
							{data?.id && (
								<Flex vertical className="w-full">
									<OrderLineItemTickets lineItemId={data.id as string} />
								</Flex>
							)}

							<div className="mb-10 mt-10">
								<Title level={4}>Documentation</Title>
								{data?.id && (
									<Card>
										<Flex gap="large" align="start" wrap="wrap">
											<ProductionTravelerButton
												lineItemId={data.id as string}
												// Allow downloading the traveler for Completed items
												// (the backend places no status gate on generation, and a
												// finished traveler is exactly the one staff need to print).
												// Still blocked for On Hold / Cancelled.
												isDisabled={
													data?.status === 'On Hold' ||
													data?.status === 'Cancelled'
												}
											/>
											<BallooningFAIButton
												lineItemId={data.id as string}
												isDisabled={data.ballooning_status !== 'Completed'}
												fileName={`${[
													data?.order?.name,
													data?.name,
													'Balloon-FAI',
												]
													.filter(Boolean)
													.join('_')
													.replace(/[\\/:*?"<>|]+/g, '')
													.replace(/\s+/g, '_')}.pdf`}
											/>
											<MaterialCertificatesButton
												lineItemId={data.id as string}
												isDisabled={disableMaterialCertificates}
												fileName={`${[data?.order?.name, data?.name, 'Cert']
													.filter(Boolean)
													.join('_')
													.replace(/[\\/:*?"<>|]+/g, '')
													.replace(/\s+/g, '_')}.pdf`}
											/>
											<QualityPacketButton
												lineItemId={data.id as string}
												isDisabled={
													disableMaterialCertificates ||
													data.ballooning_status !== 'Completed'
												}
												fileName={`${[data?.order?.name, data?.name, 'Packet']
													.filter(Boolean)
													.join('_')
													.replace(/[\\/:*?"<>|]+/g, '')
													.replace(/\s+/g, '_')}.pdf`}
											/>
											<SyncToVendorAppButton
												lineItemId={data.id as string}
												isDisabled={data.ballooning_status !== 'Completed'}
											/>
										</Flex>
									</Card>
								)}
							</div>

							<Flex vertical className="w-full mb-10">
								<Title level={4}>
									Order History{' '}
									<Tooltip title="The Order History is based on Part Name and Solid Model Volume.">
										<QuestionCircleTwoTone />
									</Tooltip>
								</Title>
								<Card>
									<OrderLineItemsTable
										filterByPreviouslyOrderedParts={true}
										lineItemId={id}
										hideFilters={true}
									/>
								</Card>
							</Flex>

							<Flex vertical className="w-full">
								<div className="mb-10">
									<Flex justify="space-between">
										<Title level={4} className="mt-10">
											Manufacturing Processes
										</Title>
										{data?.manufacturing_route ? (
											<Button
												type="primary"
												icon={<PlusOutlined />}
												onClick={() =>
													dispatch(showAddManufacturingProcessModal())
												}
											>
												Add Process
											</Button>
										) : null}
									</Flex>
									{data?.manufacturing_route ? (
										<>
											{data?.manufacturing_route?.processes?.map(
												(process, index) => (
													<div key={index} className="mb-3">
														<Card>
															<Descriptions
																title={`Process ${process.process_number}`}
																size={'small'}
																style={{ marginBottom: '1rem' }}
																// extra={[
																// 	<Button
																// 		key={1}
																// 		type="link"
																// 		icon={<EditFilled />}
																// 		size="small"
																// 		onClick={() => {
																// 			setSelectedProcess(process);
																// 			dispatch(showEditManufacturingProcessModal());
																// 		}}
																// 	>
																// 		Edit
																// 	</Button>,
																// ]}
															>
																<Descriptions.Item label="Type">
																	{process?.process_type?.display_name}
																</Descriptions.Item>
																<Descriptions.Item label="Machine">
																	<Link to={`/machines/${process.machine.id}`}>
																		{process?.machine.name}
																	</Link>
																</Descriptions.Item>
															</Descriptions>
															{process?.description ? (
																<Descriptions
																	layout="vertical"
																	style={{ marginBottom: '1rem' }}
																>
																	<Descriptions.Item
																		label="Description"
																		style={{ paddingBottom: '0.5rem' }}
																	>
																		{process?.description}
																	</Descriptions.Item>
																</Descriptions>
															) : null}
															<Title level={5}>Setups</Title>
															<Table
																key={index}
																size="small"
																dataSource={process.setups}
																pagination={false}
																className="border mb-4"
																columns={[
																	{
																		title: 'Setup No.',
																		width: 150,
																		render: (record) => (
																			<>
																				<Tag
																					color="geekblue"
																					style={{ fontWeight: 'bold' }}
																				>
																					{record.setup_number}
																				</Tag>
																				{record.is_dovetailing_setup ? (
																					<Tag
																						color="cyan"
																						style={{ fontWeight: 'bold' }}
																					>
																						Dovetailing
																					</Tag>
																				) : null}
																			</>
																		),
																	},
																	{
																		title:
																			process?.process_type?.display_name ===
																			'Milling'
																				? 'Vise'
																				: process?.process_type
																						?.display_name === 'Turning'
																				? 'Spindle'
																				: null,
																		render: (record) =>
																			process?.process_type?.display_name ===
																			'Milling'
																				? `${record?.vise?.vise_number} - ${record?.vise?.mobility}`
																				: process?.process_type
																						?.display_name === 'Turning'
																				? `${record?.spindle?.type}`
																				: null,
																	},
																	{
																		title: 'Jaw Set',
																		render: (record) =>
																			record?.jaw_set?.display_name,
																	},
																	{
																		title: 'Stock Facing Amount',
																		width: 200,
																		render: (record) =>
																			record?.stock_facing_amount
																				? `${record?.stock_facing_amount} ${record?.stock_facing_amount_unit?.name}`
																				: null,
																	},
																]}
															/>

															{process?.can_create_vsc_program ? (
																<Button
																	type="primary"
																	onClick={() => {
																		setSelectedProcess(process);
																		setConfirmVSCProgramCreationModalVisible(
																			true
																		);
																	}}
																>
																	Create Robot Program
																</Button>
															) : null}
														</Card>
													</div>
												)
											)}
										</>
									) : (
										<Card>
											<Empty
												imageStyle={{ height: 60 }}
												description={
													<span>No Manufacturing Processes Defined</span>
												}
											>
												<Button
													type="primary"
													icon={<PlusOutlined />}
													onClick={() =>
														dispatch(showAddManufacturingProcessModal())
													}
												>
													Add Process
												</Button>
											</Empty>
										</Card>
									)}
								</div>
							</Flex>
						</div>
					</>
				)}
			</div>

			{id ? (
				<AddManufacturingProcessModal
					onSubmit={onFormSubmit}
					loading={result.isLoading}
				/>
			) : null}

			{selectedProcess ? (
				<Modal
					title={'Proceed with Robot Program Creation?'}
					open={confirmVSCProgramCreationModalVisible}
					onCancel={() => setConfirmVSCProgramCreationModalVisible(false)}
					centered={true}
					width={720}
					styles={{ header: { marginBottom: '2rem' } }}
					closeIcon={false}
					maskClosable={!createVSCProgramResult.isLoading}
					cancelButtonProps={{
						onClick: () => setConfirmVSCProgramCreationModalVisible(false),
						disabled: createVSCProgramResult.isLoading,
					}}
					okText="Continue"
					okButtonProps={{
						form: 'create-vsc-program-form',
						htmlType: 'submit',
						type: 'primary',
						loading: createVSCProgramResult.isLoading,
						onClick: () => onClickCreateVSCProgramHandler(selectedProcess),
					}}
				>
					<Form
						form={createVSCProgramForm}
						name="create-vsc-program-form"
						layout="vertical"
						initialValues={{ overwrite_current: true }}
					>
						<Form.Item name="overwrite_current" valuePropName="checked">
							<Checkbox disabled={true}>Overwrite Current Program</Checkbox>
						</Form.Item>
						<Typography.Paragraph>
							WARNING: This will create a VSC configuration on{' '}
							<strong>
								{selectedProcess?.machine.mill_automation_system?.display_name}
							</strong>
							, and overwrite the current program.
						</Typography.Paragraph>
					</Form>
				</Modal>
			) : null}

			{messageContext}
			{notificationContext}
			{modalContext}
		</>
	);
};

export default OrderLineItemDetails;
